/**
 * @spec docs/superpowers/specs/2026-10-13-wallet-network-awareness-design.md §5.1
 * @standard docs/standards/staking-ui-wallet-actions.md#ui-gate
 * Always-visible header indicator of the selected wallet's network, judged as the staking chain's
 * signature flows judge it (ACTIONS.stake()). Forms gate on their own action (NetworkGatedButton);
 * this pill is information plus a shortcut to switch. Polygon is not flagged: legacy USDT claims
 * still sign there, and every form that needs Base asks for the switch itself. External wallets
 * only: the embedded wallet is moved to whichever chain an action needs without the user seeing it.
 */
import { useEffect, useId, useRef } from 'react';
import { useActionGate } from '@/hooks/useActionGate';
import { ACTIONS } from '@/lib/wallet-actions/actions';
import { CHAIN_CONFIG, LEGACY_POLYGON_CONFIG } from '@/lib/chain-registry';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';
import BaseIcon from '@/assets/icons/base.svg?react';
import PolygonIcon from '@/assets/icons/polygon.svg?react';
import WarningIcon from '@/assets/icons/warning.svg?react';

const PILL = 'flex items-center gap-1.5 h-8 px-3 rounded-full text-sm font-bold whitespace-nowrap';

function ChainIcon({ chainId }: { chainId: number | null }) {
  if (chainId === CHAIN_CONFIG.BASE.id || chainId === CHAIN_CONFIG.BASE_SEPOLIA.id)
    return <BaseIcon className="size-4" aria-hidden="true" />;
  if (chainId === LEGACY_POLYGON_CONFIG.id)
    return <PolygonIcon className="size-4" aria-hidden="true" />;
  return <span className="size-4 rounded-full bg-white/20" aria-hidden="true" />;
}

type PillStatus = 'block' | 'warn' | 'ok';

export function NetworkPill() {
  const gate = useActionGate(ACTIONS.stake());
  const status: PillStatus =
    gate.currentChainId === LEGACY_POLYGON_CONFIG.id
      ? 'ok'
      : gate.decision === 'ask'
        ? 'block'
        : gate.decision === 'warn'
          ? 'warn'
          : 'ok';
  const descriptionId = useId();
  const pillRef = useRef<HTMLSpanElement>(null);
  // The button unmounts once the switch lands, which drops focus to <body>. If the user had used
  // it and not moved on, put focus on the pill that replaced it (tabIndex -1: focusable, not a
  // tab stop). Tabbing past it disarms, so a switch started elsewhere keeps its own focus.
  const focusArmed = useRef(false);
  const pressing = useRef(false);
  useEffect(() => {
    if (status === 'block' || !focusArmed.current) return;
    focusArmed.current = false;
    const active = document.activeElement;
    if (!active || active === document.body) pillRef.current?.focus();
  }, [status]);

  if (!gate.wallet || gate.walletKind === 'embedded') return null;

  const current = gate.currentChainName ?? 'Unknown network';

  if (status === 'block') {
    return (
      <Tooltip text={`Your wallet is on ${current}`}>
        <button
          type="button"
          data-testid="network-pill"
          data-status="block"
          onPointerDown={() => {
            pressing.current = true;
          }}
          onFocus={(e) => {
            focusArmed.current = true;
            // Focus from a press is not a keyboard visit: keep the Tooltip wrapper from opening on
            // it, since the click below starts the switch and never closes the tooltip
            if (pressing.current) e.stopPropagation();
            pressing.current = false;
          }}
          onBlur={(e) => {
            // Focus went to another control; a blur with no next target (the button disabling
            // while the switch runs) keeps it armed
            if (e.relatedTarget) focusArmed.current = false;
          }}
          onClick={(e) => {
            // The Tooltip wrapper toggles on click; this click starts the switch instead.
            e.stopPropagation();
            pressing.current = false;
            focusArmed.current = true;
            void gate.switchToRequired();
          }}
          disabled={gate.isSwitching}
          aria-busy={gate.isSwitching}
          aria-describedby={descriptionId}
          className={cn(PILL, 'bg-status-error/15 text-status-error hover:bg-status-error/25')}
        >
          <WarningIcon
            className={cn('size-4', gate.isSwitching && 'animate-pulse')}
            aria-hidden="true"
          />
          <span className="sr-only sm:not-sr-only">Wrong network</span>
        </button>
        <span id={descriptionId} className="sr-only">
          Your wallet is on {current}
        </span>
      </Tooltip>
    );
  }

  if (status === 'warn') {
    return (
      <Tooltip
        text={`Your WalletConnect session reports ${current}. If signing fails, reconnect and select a ${gate.requiredChainName} account.`}
      >
        <span
          data-testid="network-pill"
          data-status="warn"
          className={cn(PILL, 'bg-yellow-500/15 text-yellow-400')}
        >
          <ChainIcon chainId={gate.currentChainId} />
          <span className="sr-only sm:not-sr-only">{current}</span>
        </span>
      </Tooltip>
    );
  }

  return (
    <span
      ref={pillRef}
      tabIndex={-1}
      data-testid="network-pill"
      data-status="ok"
      className={cn(PILL, 'bg-white/5 text-white/70')}
    >
      <ChainIcon chainId={gate.currentChainId} />
      <span className="sr-only sm:not-sr-only">{current}</span>
    </span>
  );
}
